import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { formatUnit } from "@/i18n/units";
import { useTutorial } from "@/contexts/TutorialContext";
import { ArrowLeft, Clock, Users, Flame, Heart, Loader2, ChefHat, Minus, Plus, ShoppingCart, Check, AlertCircle, ExternalLink, Save, CalendarPlus } from "lucide-react";
import { toLocalDateStr } from "@/lib/utils";
import TutorialTooltip from "@/components/TutorialTooltip";

export default function RecipePage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const planId = searchParams.get("plan_id");
  const mealDate = searchParams.get("date");
  const { api } = useAuth();
  const { t, lang } = useLanguage();
  const { tutorialActive, tutorialStep, updateAppState, goToStep } = useTutorial();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [instructionsLoading, setInstructionsLoading] = useState(false);
  const instructionPollRef = useRef(null);
  const [isFav, setIsFav] = useState(false);
  const [ingContext, setIngContext] = useState([]);
  
  // Servings state
  const [servings, setServings] = useState(null);
  const [pendingServings, setPendingServings] = useState(null); // Unsaved servings
  const [originalServings, setOriginalServings] = useState(null);
  const [scalingSaving, setScalingSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  // Nutrition state (dynamic)
  const [nutrition, setNutrition] = useState(null);
  const [scaledIngredients, setScaledIngredients] = useState([]);
  
  const [addingToCart, setAddingToCart] = useState(false);
  const [cartSuccess, setCartSuccess] = useState(false);
  const [markingDone, setMarkingDone] = useState(false);
  const [showScoreModal, setShowScoreModal] = useState(false);
  
  // Add to Plan modal state
  const [showAddToPlanModal, setShowAddToPlanModal] = useState(false);
  const [addToPlanDate, setAddToPlanDate] = useState(() => toLocalDateStr());
  const [addToPlanMealType, setAddToPlanMealType] = useState("lunch");
  const [addingToPlan, setAddingToPlan] = useState(false);

  // Update tutorial state when entering recipe page
  useEffect(() => {
    updateAppState({ currentPage: location.pathname });
  }, [location.pathname, updateAppState]);

  // Auto-skip step 3 if no missing ingredients
  useEffect(() => {
    if (tutorialActive && tutorialStep === 3 && ingContext.length > 0) {
      const missing = ingContext.filter((i) => i.status === "missing" || i.status === "insufficient");
      if (missing.length === 0) {
        goToStep(4);
      }
    }
  }, [tutorialActive, tutorialStep, ingContext, goToStep]);

  useEffect(() => {
    const fetchRecipe = async () => {
      try {
        let url = `/recipes/${id}?lang=${lang}`;
        if (planId && mealDate) url += `&plan_id=${planId}&date=${mealDate}`;
        const res = await api().get(url);
        setRecipe(res.data);
        const recipeServings = res.data?.servings || 2;
        setServings(recipeServings);
        setPendingServings(recipeServings);
        setOriginalServings(res.data?.original_servings || recipeServings);
        setScaledIngredients(res.data?.ingredients || []);
        if (res.data) {
          setNutrition({ cal: res.data.calories || 0, p: res.data.protein || 0, f: res.data.fat || 0, c: res.data.carbs || 0 });
        }

        // If no instructions yet — poll until ready (backend generates in background)
        if (!res.data?.instructions?.length) {
          setInstructionsLoading(true);
          let attempts = 0;
          const poll = async () => {
            attempts++;
            try {
              const r = await api().get(`/recipes/${id}/instructions`);
              if (r.data?.ready) {
                // Instructions ready → re-fetch the full recipe so nutrition
                // (protein/carbs/fat, filled by the same background task) shows up
                // without the user having to re-open the recipe.
                let fullUrl = `/recipes/${id}?lang=${lang}`;
                if (planId && mealDate) fullUrl += `&plan_id=${planId}&date=${mealDate}`;
                try {
                  const full = await api().get(fullUrl);
                  setRecipe(full.data);
                  setScaledIngredients(full.data?.ingredients || []);
                  setNutrition({
                    cal: full.data?.calories || 0,
                    p: full.data?.protein || 0,
                    f: full.data?.fat || 0,
                    c: full.data?.carbs || 0,
                  });
                } catch {
                  // Fallback: at least render the instructions we already have
                  setRecipe(prev => ({ ...prev, instructions: r.data.instructions }));
                }
                setInstructionsLoading(false);
                return;
              }
            } catch {}
            if (attempts < 8) {
              instructionPollRef.current = setTimeout(poll, 2500);
            } else {
              setInstructionsLoading(false);
            }
          };
          instructionPollRef.current = setTimeout(poll, 2500);
        }
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    fetchRecipe();
    return () => clearTimeout(instructionPollRef.current);
  }, [id, planId, mealDate, api, lang]);

  const fetchContext = useCallback(async (overrideServings) => {
    try {
      let url = `/recipes/${id}/ingredient-context`;
      const params = [`lang=${lang}`];
      if (planId && mealDate) {
        params.push(`plan_id=${planId}`, `date=${mealDate}`);
      }
      if (overrideServings) {
        params.push(`servings=${overrideServings}`);
      }
      if (params.length) url += `?${params.join("&")}`;
      const res = await api().get(url);
      setIngContext(res.data || []);
    } catch (err) { console.error(err); }
  }, [id, planId, mealDate, api, lang]);

  useEffect(() => { fetchContext(); }, [fetchContext, lang]);

  // Re-run fridge/cart comparison once ingredients arrive for a just-generated
  // recipe (background fill / poll). Without this the fridge/cart statuses only
  // appear on re-entry.
  useEffect(() => {
    if (recipe?.ingredients?.length) fetchContext();
  }, [recipe?.ingredients?.length, fetchContext]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setShowScoreModal(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    const checkFav = async () => {
      try {
        const res = await api().get(`/favorites/check/${id}`);
        setIsFav(res.data.favorited);
      } catch (err) { console.error(err); }
    };
    checkFav();
  }, [id, api]);

  const toggleFav = async () => {
    try {
      const res = await api().post(`/favorites/${id}`);
      setIsFav(res.data.favorited);
    } catch (err) { console.error(err); }
  };

  // Change pending servings (doesn't save yet)
  const changePendingServings = (delta) => {
    const newServings = Math.max(1, Math.min(20, (pendingServings || 2) + delta));
    setPendingServings(newServings);
    setHasUnsavedChanges(newServings !== servings);
    
    // recipe.servings and recipe.ingredients always correspond to each other
    // (set once on load — base recipe or plan-overlaid version)
    const loadedServings = recipe?.servings || originalServings || 2;
    const ratio = newServings / loadedServings;
    
    // Scale nutrition locally (preview) — recipe.calories corresponds to recipe.servings
    const baseNutrition = {
      cal: recipe?.calories || 0,
      p: recipe?.protein || 0,
      f: recipe?.fat || 0,
      c: recipe?.carbs || 0,
    };
    setNutrition({
      cal: Math.round(baseNutrition.cal * ratio),
      p: Math.round(baseNutrition.p * ratio * 10) / 10,
      f: Math.round(baseNutrition.f * ratio * 10) / 10,
      c: Math.round(baseNutrition.c * ratio * 10) / 10,
    });
    
    // Scale ingredients locally from recipe.ingredients (loaded amounts)
    const sourceIngredients = recipe?.ingredients || [];
    if (sourceIngredients.length > 0) {
      const scaled = sourceIngredients.map(ing => {
        const origAmount = parseFloat(String(ing.amount || "0").replace(",", ".")) || 0;
        const scaledAmount = origAmount * ratio;
        return {
          ...ing,
          amount: scaledAmount === Math.floor(scaledAmount) 
            ? String(Math.floor(scaledAmount)) 
            : String(Math.round(scaledAmount * 10) / 10),
        };
      });
      setScaledIngredients(scaled);
    }
    
    // For non-plan recipes, consider it "saved" immediately and refresh context
    if (!planId) {
      setServings(newServings);
      setHasUnsavedChanges(false);
      fetchContext(newServings);
    }
  };

  // Save servings - triggers recalculation
  const saveServings = async () => {
    if (!planId || !mealDate || pendingServings === servings) return;
    
    setScalingSaving(true);
    try {
      const res = await api().put("/meal-plans/servings", {
        plan_id: planId,
        date: mealDate,
        recipe_id: id,
        servings: pendingServings,
      });
      
      if (res.data.status === "ok") {
        // Update local state with response
        setServings(pendingServings);
        setHasUnsavedChanges(false);
        
        // Update nutrition
        if (res.data.nutrition) {
          setNutrition(res.data.nutrition);
        }
        
        // Update scaled ingredients
        if (res.data.ingredients) {
          setScaledIngredients(res.data.ingredients);
        }
        
        // Update ingredient context
        if (res.data.ingredient_context) {
          setIngContext(res.data.ingredient_context);
        }
        
        localStorage.setItem("meal_plan_changed", "true");
      }
    } catch (err) { 
      console.error(err);
      // Revert on error
      setPendingServings(servings);
      setHasUnsavedChanges(false);
    }
    setScalingSaving(false);
  };
const addMissingToCart = async () => {
    const missing = ingContext.filter((i) => i.status === "missing" || i.status === "insufficient");
    if (!missing.length) return;
    setAddingToCart(true);
    let addedCount = 0;
    try {
      let listId = null;
      const listRes = await api().get("/grocery-list");
      
      if (listRes.data?.id) {
        listId = listRes.data.id;
      } else {
        const createRes = await api().post("/grocery-list/create-empty");
        listId = createRes.data?.id;
      }
      
      if (!listId) return;
      
      const itemsToAdd = missing.map(ing => ({
        name: ing.name,
        amount: ing.deficit > 0 ? String(ing.deficit) : (ing.amount || "1"),
        unit: ing.unit || "шт",
      }));
      
      const batchRes = await api().post(`/grocery-list/${listId}/add-items-batch`, {
        items: itemsToAdd,
      });
      
      // DEBUG: log to see what backend returns
      console.log("[addMissingToCart] sent:", itemsToAdd.length, "added:", batchRes.data?.added_count, "response:", batchRes.data);
      
      addedCount = batchRes.data?.added_count || itemsToAdd.length;
      updateAppState({ hasGroceryItems: true });
      
      if (tutorialActive && tutorialStep === 3) {
        goToStep(4);
      }
    } catch (err) { 
      console.error("addMissingToCart error:", err); 
    } finally {
      setAddingToCart(false);  // ← Always reset, no matter what
    }
    
    try { await fetchContext(); } catch (e) {}
  };
  
  const handleMarkDone = async (rating = null) => {
    if (!planId || !mealDate) return;
    setMarkingDone(true);
    setShowScoreModal(false);
    try {
      await api().post("/meal-plans/mark-done", {
        plan_id: planId,
        date: mealDate,
        recipe_id: id,
        rating,
      });
      if (tutorialActive && tutorialStep === 10) {
        goToStep(11);
      }
      navigate("/");
    } catch (err) { console.error(err); }
    setMarkingDone(false);
  };

  const handleMarkDoneClick = () => {
    if (!planId || !mealDate) return;
    // Tutorial step 10: "Done" must complete in one tap (subtract ingredients + finish tutorial),
    // matching the tooltip promise. Skip the rating modal in tutorial flow.
    if (tutorialActive && tutorialStep === 10) {
      handleMarkDone(null);
      return;
    }
    setShowScoreModal(true);
  };

  // Add recipe to meal plan
  const handleAddToPlan = async () => {
    if (!addToPlanDate || !addToPlanMealType) return;
    setAddingToPlan(true);
    try {
      // First ensure a plan exists for that date
      const createRes = await api().post("/meal-plans/create-empty", { date: addToPlanDate });
      
      // Try to get plan_id from create response first
      let planIdToUse = createRes.data?.plan_id;
      
      // If not in response, search in existing plans
      if (!planIdToUse) {
        const plansRes = await api().get("/meal-plans");
        const plans = plansRes.data || [];
        
        // Search for plan that contains the target date
        for (const p of plans) {
          const hasDate = p.days?.some(d => d.date === addToPlanDate);
          if (hasDate) {
            planIdToUse = p.id;
            break;
          }
        }
      }
      
      if (planIdToUse) {
        // Add this recipe to the plan
        const addRes = await api().post("/meal-plans/add-existing-recipe", {
          plan_id: planIdToUse,
          date: addToPlanDate,
          meal_type: addToPlanMealType,
          recipe_id: id,
        });
        
        if (addRes.data?.status === "ok") {
          setShowAddToPlanModal(false);
          // Navigate to planner
          navigate("/planner");
        } else {
          console.error("Failed to add recipe to plan:", addRes.data);
        }
      } else {
        console.error("Could not find or create plan for date:", addToPlanDate);
      }
    } catch (err) { 
      console.error("Error adding to plan:", err); 
    }
    setAddingToPlan(false);
  };

  const findCtx = (ing) => {
    // src_name is the reliable key: the backend carries the stored name into
    // every translated ingredient, and the context emits it unchanged. Fall
    // back to name/localized_name for recipes whose translation was cached
    // before src_name existed.
    const keys = [ing?.src_name, ing?.name, ing?.localized_name]
      .map((v) => String(v || "").toLowerCase())
      .filter(Boolean);
    if (!keys.length) return undefined;
    return ingContext.find((c) => {
      const cand = [c?.src_name, c?.name, c?.localized_name]
        .map((v) => String(v || "").toLowerCase())
        .filter(Boolean);
      return cand.some((x) => keys.includes(x));
    });
  };

  const getIngStatus = (ing) => findCtx(ing)?.status;
  const getIngDeficit = (ing) => findCtx(ing)?.deficit || 0;

  const missingCount = ingContext.filter((i) => i.status === "missing" || i.status === "insufficient").length;
  
  // Use scaled ingredients if available, otherwise recipe ingredients
  const displayIngredients = scaledIngredients.length > 0 ? scaledIngredients : (recipe?.ingredients || []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF8] pb-28 animate-pulse">
        <div className="pl-[72px] pr-5 pt-6 pb-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#E8E5E0]" />
          <div className="h-5 w-32 bg-[#E8E5E0] rounded-full" />
        </div>
        <div className="w-full h-52 bg-[#E8E5E0]" />
        <div className="px-5 pt-5 space-y-3">
          <div className="h-7 w-3/4 bg-[#E8E5E0] rounded-full" />
          <div className="h-4 w-1/2 bg-[#E8E5E0] rounded-full" />
          <div className="flex gap-2 mt-4">
            {[1,2,3].map(i => <div key={i} className="h-8 w-20 bg-[#E8E5E0] rounded-full" />)}
          </div>
          <div className="mt-6 space-y-2">
            {[1,2,3,4].map(i => <div key={i} className="h-12 bg-[#E8E5E0] rounded-2xl" />)}
          </div>
        </div>
      </div>
    );
  }

  if (!recipe) {
    return (
      <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center px-5">
        <p className="font-body text-[#6B705C]">Recipe not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF8] pb-28" data-testid="recipe-page">

      {/* Header - Back button, Title with Heart, Add to Plan */}
      <div className="pl-[72px] pr-5 pt-6 pb-4 flex items-center gap-3">
        <button
          data-testid="recipe-back-button"
          aria-label={t("back") || "Назад"}
          onClick={() => {
            if (hasUnsavedChanges && !window.confirm(t("unsavedServingsWarning") || "Зміни порцій не збережено. Покинути без збереження?")) return;
            navigate(-1);
          }}
          className="w-10 h-10 rounded-full bg-white border border-[#E8E5E0] flex items-center justify-center shrink-0"
        >
          <ArrowLeft className="w-4 h-4 text-[#6B705C]" strokeWidth={1.5} />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="font-heading text-xl text-[#3D4035] truncate">{t("recipeDetails")}</h1>
        </div>
        {/* Add to Plan button - only show if not already in a plan */}
        {!planId && (
          <button
            data-testid="add-to-plan-btn"
            onClick={() => setShowAddToPlanModal(true)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-[#7B9E7F] text-white font-body text-xs font-semibold hover:bg-[#6B8E6F] transition-all shrink-0"
          >
            <CalendarPlus className="w-4 h-4" strokeWidth={1.5} />
            {t("addToPlan") || "Add to Plan"}
          </button>
        )}
      </div>

      {/* Recipe Header */}
      <div className="px-5 mb-6">
        <div className="bg-white rounded-3xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-black/5">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-14 h-14 rounded-2xl bg-[#7B9E7F]/10 flex items-center justify-center shrink-0">
              <ChefHat className="w-7 h-7 text-[#7B9E7F]" strokeWidth={1.5} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h2 className="font-heading text-2xl text-[#3D4035] truncate">{recipe.title}</h2>
                <button
                  data-testid="recipe-favorite-button"
                  onClick={toggleFav}
                  className={`w-8 h-8 rounded-full flex items-center justify-center transition-all shrink-0 ${
                    isFav ? "bg-[#D4856B] text-white" : "bg-[#F2E9E4] text-[#A5A58D] hover:bg-[#E8E5E0]"
                  }`}
                >
                  <Heart className="w-4 h-4" strokeWidth={1.5} fill={isFav ? "currentColor" : "none"} />
                </button>
              </div>
              {recipe.cuisine && <p className="font-body text-sm text-[#A5A58D]">{t(recipe.cuisine)}</p>}
            </div>
          </div>

          {recipe.description && (
            <p className="font-body text-[#6B705C] mb-4">{recipe.description}</p>
          )}

          {/* Stats */}
          <div className="flex gap-4 flex-wrap">
            {recipe.prep_time && (
              <div className="flex items-center gap-1.5 text-sm font-body text-[#6B705C] bg-[#F2E9E4] px-3 py-1.5 rounded-full">
                <Clock className="w-3.5 h-3.5" strokeWidth={1.5} />
                {t("prepTime")}: {recipe.prep_time} {t("min")}
              </div>
            )}
            {recipe.cook_time && (
              <div className="flex items-center gap-1.5 text-sm font-body text-[#6B705C] bg-[#F2E9E4] px-3 py-1.5 rounded-full">
                <Clock className="w-3.5 h-3.5" strokeWidth={1.5} />
                {t("cookTime")}: {recipe.cook_time} {t("min")}
              </div>
            )}
            {servings && (
              <div className="flex items-center gap-1.5 text-sm font-body text-[#6B705C] bg-[#F2E9E4] px-3 py-1.5 rounded-full">
                <Users className="w-3.5 h-3.5" strokeWidth={1.5} />
                {servings} {t("servings")}
              </div>
            )}
          </div>

          {/* Servings Control with Save Button */}
          <div className="mt-4 pt-4 border-t border-[#F2E9E4]" data-testid="servings-control">
            <div className="flex items-center justify-between">
              <span className="font-body text-sm text-[#6B705C] font-semibold">{t("servings")} {t("servingsFor")}:</span>
              <div className="flex items-center gap-2">
                <button 
                  data-testid="servings-minus" 
                  onClick={() => changePendingServings(-1)}
                  className="w-9 h-9 rounded-full bg-[#EBE9E4] flex items-center justify-center hover:bg-[#E0DDD8] transition-colors"
                >
                  <Minus className="w-4 h-4 text-[#6B705C]" strokeWidth={2} />
                </button>
                <span 
                  className={`font-body font-bold text-lg w-8 text-center ${hasUnsavedChanges ? 'text-[#E9C46A]' : 'text-[#3D4035]'}`} 
                  data-testid="servings-value"
                >
                  {pendingServings || 2}
                </span>
                <button 
                  data-testid="servings-plus" 
                  onClick={() => changePendingServings(1)}
                  className="w-9 h-9 rounded-full bg-[#7B9E7F] flex items-center justify-center hover:bg-[#6B8E6F] transition-colors"
                >
                  <Plus className="w-4 h-4 text-white" strokeWidth={2} />
                </button>
                <span className="font-body text-sm text-[#A5A58D]">{t("persons")}</span>
                
                {/* Save Button */}
                {planId && mealDate && (
                  <button
                    data-testid="save-servings-btn"
                    onClick={saveServings}
                    disabled={!hasUnsavedChanges || scalingSaving}
                    className={`ml-2 px-3 py-1.5 rounded-full font-body text-xs font-bold flex items-center gap-1.5 transition-all ${
                      hasUnsavedChanges 
                        ? 'bg-[#7B9E7F] text-white hover:bg-[#6B8E6F] shadow-md' 
                        : 'bg-[#E8E5E0] text-[#A5A58D] cursor-not-allowed'
                    }`}
                  >
                    {scalingSaving ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <Save className="w-3 h-3" strokeWidth={2} />
                    )}
                    {t("save")}
                  </button>
                )}
              </div>
            </div>
            {hasUnsavedChanges && planId && (
              <p className="mt-2 text-xs text-[#E9C46A] font-body">{t("unsavedServingsChanges")}</p>
            )}
          </div>
        </div>
      </div>

      {/* Dynamic Nutrition - Perfectly centered */}
      {nutrition && (nutrition.cal > 0 || nutrition.p > 0 || nutrition.c > 0 || nutrition.f > 0) && (
        <div className="px-5 mb-6" data-testid="recipe-nutrition">
          <h3 className="font-body text-xs text-[#6B705C] uppercase tracking-widest font-bold mb-3">{t("nutrition")}</h3>
          <div className="grid grid-cols-4 gap-2">
            <div className="bg-[#D4856B]/10 rounded-2xl p-3 flex flex-col items-center justify-center min-h-[88px]">
              <Flame className="w-4 h-4 text-[#D4856B] mb-1" strokeWidth={1.5} />
              <p className="font-body font-bold text-[#3D4035] text-lg leading-tight" data-testid="nutrition-cal">
                {Math.round(nutrition.cal)}
              </p>
              <p className="font-body text-[10px] text-[#A5A58D] uppercase font-bold tracking-wider">{t("cal")}</p>
            </div>
            <div className="bg-[#5C7A5E]/10 rounded-2xl p-3 flex flex-col items-center justify-center min-h-[88px]">
              <p className="font-body font-bold text-[#3D4035] text-lg leading-tight" data-testid="nutrition-protein">
                {Math.round(nutrition.p)}g
              </p>
              <p className="font-body text-[10px] text-[#A5A58D] uppercase font-bold tracking-wider">{t("protein")}</p>
            </div>
            <div className="bg-[#E9C46A]/10 rounded-2xl p-3 flex flex-col items-center justify-center min-h-[88px]">
              <p className="font-body font-bold text-[#3D4035] text-lg leading-tight" data-testid="nutrition-carbs">
                {Math.round(nutrition.c)}g
              </p>
              <p className="font-body text-[10px] text-[#A5A58D] uppercase font-bold tracking-wider">{t("carbs")}</p>
            </div>
            <div className="bg-[#F2E9E4] rounded-2xl p-3 flex flex-col items-center justify-center min-h-[88px]">
              <p className="font-body font-bold text-[#3D4035] text-lg leading-tight" data-testid="nutrition-fat">
                {Math.round(nutrition.f)}g
              </p>
              <p className="font-body text-[10px] text-[#A5A58D] uppercase font-bold tracking-wider">{t("fat")}</p>
            </div>
          </div>
          {recipe?.nutrition_total > 0 &&
            recipe?.nutrition_resolved != null &&
            recipe.nutrition_resolved < recipe.nutrition_total && (
              <p className="font-body text-xs text-[#A5A58D] mt-2" data-testid="nutrition-partial">
                {t("nutritionPartial")
                  .replace("{resolved}", recipe.nutrition_resolved)
                  .replace("{total}", recipe.nutrition_total)}
              </p>
            )}
        </div>
      )}

      {/* Ingredients */}
      {displayIngredients?.length > 0 && (
        <div className="px-5 mb-6" data-testid="recipe-ingredients">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-body text-xs text-[#6B705C] uppercase tracking-widest font-bold">{t("ingredients")}</h3>
            <div className="flex items-center gap-2">
              {missingCount > 0 && (
                <button 
                  data-testid="add-missing-to-cart" 
                  onClick={addMissingToCart} 
                  disabled={addingToCart}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-[#7B9E7F] text-white rounded-full font-body text-xs font-semibold hover:bg-[#6B8E6F] transition-all disabled:opacity-50"
                >
                  {addingToCart ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShoppingCart className="w-3 h-3" strokeWidth={2} />}
                  {t("addMissingToCart")} ({missingCount})
                </button>
              )}
              {cartSuccess && missingCount === 0 && (
                <span className="flex items-center gap-1.5 px-3 py-1.5 bg-[#7B9E7F]/15 text-[#5C7A5E] rounded-full font-body text-xs font-semibold animate-fade-in" data-testid="cart-success-msg">
                  <Check className="w-3 h-3" strokeWidth={2.5} />
                  {t("addedToCart") || "Додано в кошик!"}
                </span>
              )}
              <button 
                data-testid="go-to-cart-btn" 
                onClick={() => navigate("/grocery")}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-[#E9C46A] text-white rounded-full font-body text-xs font-semibold hover:bg-[#D4B35B] transition-all"
              >
                <ExternalLink className="w-3 h-3" strokeWidth={2} />
                {t("cart")}
              </button>
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-black/5 divide-y divide-[#F2E9E4]">
            {displayIngredients.map((ing, idx) => {
              const status = getIngStatus(ing);
              const deficit = getIngDeficit(ing);
              
              return (
                <div 
                  key={idx} 
                  data-testid={`ingredient-${idx}`}
                  className={`px-4 py-3 flex items-center justify-between ${
                    status === "missing" || status === "insufficient" ? "bg-[#BC4749]/3" : ""
                  }`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    {status === "in_fridge" && (
                      <div className="w-5 h-5 rounded-full bg-[#7B9E7F]/10 flex items-center justify-center shrink-0">
                        <Check className="w-3 h-3 text-[#7B9E7F]" strokeWidth={2} />
                      </div>
                    )}
                    {status === "in_cart" && (
                      <div className="w-5 h-5 rounded-full bg-[#E9C46A]/15 flex items-center justify-center shrink-0">
                        <ShoppingCart className="w-3 h-3 text-[#E9C46A]" strokeWidth={2} />
                      </div>
                    )}
                    {status === "insufficient" && (
                      <div className="w-5 h-5 rounded-full bg-[#E9C46A]/15 flex items-center justify-center shrink-0">
                        <AlertCircle className="w-3 h-3 text-[#E9C46A]" strokeWidth={2} />
                      </div>
                    )}
                    {status === "missing" && (
                      <div className="w-5 h-5 rounded-full bg-[#BC4749]/10 flex items-center justify-center shrink-0">
                        <AlertCircle className="w-3 h-3 text-[#BC4749]" strokeWidth={2} />
                      </div>
                    )}
                    <span className={`font-body font-medium truncate ${
                      status === "missing" ? "text-[#BC4749] underline decoration-[#BC4749]/30 underline-offset-2" :
                      status === "insufficient" ? "text-[#E9C46A]" :
                      status === "in_fridge" ? "text-[#3D4035]" :
                      status === "in_cart" ? "text-[#6B705C]" : "text-[#3D4035]"
                    }`}
                    title={ing.localized_name || ing.name}
                    >{ing.localized_name || ing.name}</span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`font-body text-sm ${
                      status === "missing" || status === "insufficient"
                        ? "text-[#BC4749] font-semibold bg-[#BC4749]/8 px-1.5 py-0.5 rounded" 
                        : "text-[#A5A58D]"
                    }`}>
                      {ing.amount} {formatUnit(ing.unit, lang)}
                    </span>
                    {status === "in_fridge" && (
                      <span className="font-body text-[10px] text-[#7B9E7F] bg-[#7B9E7F]/10 px-1.5 py-0.5 rounded">{t("inFridge")}</span>
                    )}
                    {status === "in_cart" && (
                      <span className="font-body text-[10px] text-[#E9C46A] bg-[#E9C46A]/10 px-1.5 py-0.5 rounded">{t("inCart")}</span>
                    )}
                    {status === "insufficient" && deficit > 0 && (
                      <span className="font-body text-[10px] text-[#BC4749] bg-[#BC4749]/8 px-1.5 py-0.5 rounded">
                        {t("needMore")} +{deficit}
                      </span>
                    )}
                    {status === "missing" && (
                      <span className="font-body text-[10px] text-[#BC4749] bg-[#BC4749]/10 px-1.5 py-0.5 rounded">{t("missing")}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* Tutorial Step 3 */}
          {missingCount > 0 && tutorialActive && tutorialStep === 3 && (
            <TutorialTooltip step={3} targetId="add-missing-to-cart" />
          )}
        </div>
      )}

      {/* Tutorial Step 4 */}
      {tutorialActive && tutorialStep === 4 && (
        <TutorialTooltip step={4} targetId="go-to-cart-btn" />
      )}

      {/* Instructions — skeleton while generating */}
      {instructionsLoading && (
        <div className="px-5 mb-6">
          <h3 className="font-body text-xs text-[#6B705C] uppercase tracking-widest font-bold mb-3">{t("instructions")}</h3>
          <div className="space-y-3 animate-pulse">
            {[1,2,3].map(i => (
              <div key={i} className="flex gap-3 bg-white rounded-2xl p-4 border border-black/5">
                <div className="w-8 h-8 rounded-full bg-[#E8E5E0] shrink-0" />
                <div className="flex-1 pt-1 space-y-2">
                  <div className="h-3 bg-[#E8E5E0] rounded-full w-full" />
                  <div className="h-3 bg-[#E8E5E0] rounded-full w-3/4" />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Instructions */}
      {!instructionsLoading && recipe.instructions?.length > 0 && (
        <div className="px-5 mb-6" data-testid="recipe-instructions">
          <h3 className="font-body text-xs text-[#6B705C] uppercase tracking-widest font-bold mb-3">{t("instructions")}</h3>
          <div className="space-y-3">
            {recipe.instructions.map((step, idx) => {
              const text = typeof step === "object" ? step.text : step;
              const photo = typeof step === "object" ? step.photo : null;
              return (
                <div key={idx} className="flex gap-3 bg-white rounded-2xl p-4 border border-black/5">
                  <div className="w-8 h-8 rounded-full bg-[#7B9E7F] text-white flex items-center justify-center shrink-0 font-body font-bold text-sm">
                    {idx + 1}
                  </div>
                  <div className="flex-1 pt-1">
                    <p className="font-body text-[#3D4035] text-sm leading-relaxed">{text}</p>
                    {photo && (
                      <img src={photo} alt={`${t("step")} ${idx + 1}`} className="mt-2 rounded-xl w-full max-h-48 object-cover" />
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Mark as Done button */}
          {planId && mealDate && (
            <button
              data-testid="recipe-mark-done-btn"
              aria-label={t("markAsDone")}
              onClick={handleMarkDoneClick}
              disabled={markingDone}
              className="mt-4 w-full bg-[#7B9E7F] text-white rounded-2xl py-4 font-body font-bold text-base flex items-center justify-center gap-2.5 hover:bg-[#6B8E6F] transition-all shadow-lg shadow-[#7B9E7F]/20 disabled:opacity-50 active:scale-[0.98]"
            >
              {markingDone ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" strokeWidth={2} />}
              {t("markAsDone")}
            </button>
          )}

          {/* Tutorial Step 10 */}
          {tutorialActive && tutorialStep === 10 && (
            <TutorialTooltip step={10} targetId="recipe-mark-done-btn" />
          )}
        </div>
      )}

      {/* Meal Score Modal */}
      {showScoreModal && (
        <div className="fixed inset-0 z-[10001] bg-black/50 flex items-center justify-center px-5">
          <div className="w-full max-w-sm bg-white rounded-3xl p-7 shadow-2xl text-center">
            <p className="text-3xl mb-2">🍽️</p>
            <h3 className="font-heading text-xl text-[#3D4035] mb-1">{t("mealScoreTitle") || "Як вам страва?"}</h3>
            <p className="font-body text-sm text-[#A5A58D] mb-6">{t("mealScoreSub") || "Ваша оцінка допоможе AI рекомендувати краще"}</p>
            <div className="flex gap-4 justify-center mb-5">
              <button
                onClick={() => handleMarkDone(1)}
                className="flex-1 py-4 rounded-2xl bg-[#FFF0ED] text-3xl hover:bg-[#FFDDD6] transition-colors active:scale-95"
              >
                👎
              </button>
              <button
                onClick={() => handleMarkDone(5)}
                className="flex-1 py-4 rounded-2xl bg-[#F0F7F0] text-3xl hover:bg-[#D6EDD6] transition-colors active:scale-95"
              >
                👍
              </button>
            </div>
            <button
              onClick={() => handleMarkDone(null)}
              className="w-full py-2.5 rounded-xl text-sm font-body text-[#A5A58D] hover:text-[#6B705C] transition-colors"
            >
              {t("skip") || "Пропустити"}
            </button>
          </div>
        </div>
      )}

      {/* Add to Plan Modal */}
      {showAddToPlanModal && (
        <div className="fixed inset-0 z-[10001] bg-black/40 flex items-end" data-testid="add-to-plan-modal">
          <div className="w-full max-w-[480px] mx-auto bg-[#FAFAF8] rounded-t-3xl p-6 animate-slide-up">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-heading text-xl text-[#3D4035]">{t("addToPlan") || "Add to Plan"}</h3>
              <button 
                onClick={() => setShowAddToPlanModal(false)} 
                className="w-9 h-9 rounded-full bg-[#EBE9E4] flex items-center justify-center"
              >
                <span className="text-[#6B705C] text-lg">×</span>
              </button>
            </div>
            
            {/* Recipe preview */}
            <div className="bg-white rounded-xl p-4 border border-black/5 mb-4">
              <p className="font-body font-semibold text-[#3D4035]">{recipe?.title}</p>
              {recipe?.description && (
                <p className="font-body text-xs text-[#A5A58D] mt-1 truncate">{recipe.description}</p>
              )}
            </div>

            {/* Date picker */}
            <div className="mb-4">
              <label className="font-body text-xs text-[#6B705C] uppercase tracking-widest font-bold mb-2 block">
                {t("selectDate") || "Which day?"}
              </label>
              <input
                data-testid="add-to-plan-date"
                type="date"
                value={addToPlanDate}
                onChange={(e) => setAddToPlanDate(e.target.value)}
                min={toLocalDateStr()}
                className="w-full bg-white ring-1 ring-[#E8E5E0] focus:ring-2 focus:ring-[#7B9E7F]/50 rounded-xl h-12 px-4 font-body text-[#3D4035] outline-none"
              />
            </div>

            {/* Meal type selector */}
            <div className="mb-6">
              <label className="font-body text-xs text-[#6B705C] uppercase tracking-widest font-bold mb-2 block">
                {t("selectMealType") || "Which meal?"}
              </label>
              <div className="grid grid-cols-4 gap-2">
                {["breakfast", "lunch", "dinner", "snack"].map((type) => (
                  <button
                    key={type}
                    data-testid={`meal-type-${type}`}
                    onClick={() => setAddToPlanMealType(type)}
                    className={`py-3 px-2 rounded-xl font-body text-xs font-semibold transition-all ${
                      addToPlanMealType === type
                        ? "bg-[#7B9E7F] text-white shadow-md"
                        : "bg-white text-[#6B705C] border border-[#E8E5E0] hover:border-[#7B9E7F]"
                    }`}
                  >
                    {t(type)}
                  </button>
                ))}
              </div>
            </div>

            {/* Confirm button */}
            <button
              data-testid="confirm-add-to-plan"
              onClick={handleAddToPlan}
              disabled={addingToPlan}
              className="w-full bg-[#7B9E7F] text-white rounded-full h-14 font-body font-bold text-base flex items-center justify-center gap-2 hover:bg-[#6B8E6F] transition-all disabled:opacity-50"
            >
              {addingToPlan ? <Loader2 className="w-5 h-5 animate-spin" /> : <CalendarPlus className="w-5 h-5" strokeWidth={1.5} />}
              {t("addToPlan") || "Add to Plan"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
